import type { App, Ref } from 'vue';
import { ref } from 'vue';
import { createI18n } from 'vue-i18n';

type MessageDict = Record<string, string>;
type Messages = Record<string, MessageDict>;

const STORAGE_KEY = 'app.locale';

const zhCn: Messages = {
  'zh-CN': {
    'lang.zh': '中文',
    'lang.en': 'EN',
    'header.saveProject': '保存工程',
    'header.submitProject': '提交到云端',
    'header.loadProject': '读取工程',
    'header.logout': '退出登录',
    'header.selectSharedDrive': '选择共享云端硬盘',
    'header.selectFirstLevelFolder': '选择一级文件夹',
    'header.selectSecondLevelFolder': '选择二级文件夹',
    'header.autoCompose': 'auto-compose',
    'header.autoComposeImporting': '导入中...',
    'header.noAutoSaveData': '没有可恢复的自动保存数据',
    'header.restoreAutoSaveTitle': '恢复自动保存',
    'header.restoreAutoSaveMessage': '是否恢复到 {time} 的自动保存版本？当前未保存的修改将丢失。',
    'header.confirmRestore': '恢复',
    'header.cancel': '取消',
    'header.projectEmptyNoSave': '当前工程为空，无需保存。',
    'header.projectSavedLocal': '工程已保存到本地',
    'header.saveProjectFailed': '保存工程失败',
    'header.projectEmptyNoSubmit': '当前工程为空，无需提交。',
    'header.selectDriveBeforeSubmit': '请先选择共享云端硬盘 / 一级 / 二级文件夹后再提交。',
    'header.noValidDriveTarget': '未找到有效的 Drive 目标位置，请重新选择云盘位置。',
    'header.projectSubmittedDrive': '工程已提交到云端 Drive',
    'header.submitProjectFailed': '提交工程到 Drive 失败',
    'header.loggedOut': '已退出登录',
    'player.reloggingIn': '正在重新登录...',
    'header.selectDriveFirst': '请先选择 Drive / Pipeline / Project。',
    'header.noJsonInFolder': '所选文件夹中未找到 JSON 工程文件。',
    'header.readingLatestFile': '正在读取最新文件: {name}',
    'header.findingLatestProject': '正在 Drive 中查找最新工程...',
    'header.downloadingFile': '正在下载 {name}...',
    'header.driveLoadFailed': 'Drive 加载失败',
    'header.unsupportedMp4InProject': '项目包含不支持的 MP4 格式视频。请确保所有视频文件均为 WebM 格式。',
    'header.projectLoadSuccessDetail': '读取 {name} 成功<br>当前版本由 {user} 于 {time} 最后编辑',
    'header.projectLoadSuccess': '工程读取成功',
    'header.projectLoadFailed': '读取工程失败',
    'header.panelNoAutoCompose': '当前面板暂不支持 Auto Compose',
    'header.loadingMediaFromCloud': '正在从云端加载媒体，请稍候...',
    'header.secondLevelFolderEmpty': '二级文件夹为空: {name}',
    'header.fileNotFound': '未找到文件: {path}',
    'header.jsonFormatError': 'JSON 文件格式错误或无数据',
    'header.autoComposePartialFail': 'auto-compose 完成，但有部分文件导入失败：\n{errors}',
    'header.autoComposeComplete': 'auto-compose 导入完成',
    'header.autoComposeFailed': 'auto-compose 导入失败: {error}',
    'header.audioRestoredFromCache': '成功从缓存恢复音频: {name}',
    'header.audioStreamLoaded': '成功从 Drive 流式加载音频: {name}',
    'header.audioStreamLoadedR2': '已从 R2 流式加载音频: {name}',
    'header.audioStreamLoadedDrive': 'R2 无此文件，已从 Drive 流式加载音频: {name}',
    'header.audioDownloaded': '成功从 Drive 下载音频: {name}',
    'header.audioDecodeFailed': '音频解码失败: {name}',
    'header.videoRestoredFromCache': '成功从缓存恢复视频: {name}',
    'header.videoDownloaded': '成功从 Drive 下载视频: {name}',
    'header.videoStreamLoadedR2': '已从 R2 流式加载视频: {name}',
    'header.videoStreamLoadedDrive': 'R2 无此文件，已从 Drive 流式加载视频: {name}',
    'header.largeMediaSourceSummary': '大文件读取出处',
    // RecordPanel -> Sound
    'record.login': '登录',
    'record.record': '录音',
    'record.stop': '停止',
    'record.play': '播放',
    'record.fetch': '获取',
    'record.fetching': 'Fetching...',
    'record.check': 'Check (Q)',
    'record.insert': 'Insert (W)',
    'record.rescan': '重新扫描',
    'record.clearCache': '清除缓存',
    'record.exportInfo': '导出信息',
    'record.getRef': 'Get Ref',
    'record.infer': 'Infer',
    'record.mimic': 'Mimic',
    'record.getText': 'Get Text',
    'record.speed': 'Speed',
    'record.tts': 'TTS',
    'record.speaker': 'Speaker',
    'record.currentSpeaker': 'current speaker',
    'record.pitch': 'Pitch',
    'record.autopitch': '自动',
    'record.clone': 'Clone',
    'record.get': 'Get',
    'record.send': 'Send',
    'record.scanning': '扫描中...',
    'record.importing': '正在导入参考视频...',
    'record.processing': '正在进行声音合成，请等待...',
    // TextPanel
    'text.localSrt': '本地上传SRT',
    'text.localImport': '本地导入',
    'text.importFromDrive': '从Drive导入',
    'text.importRefVideo': '导入参考视频',
    'text.driveRefVideo': 'Drive参考视频',
    'text.addSubtitle': '添加字幕',
    'text.splitSubtitle': '分割字幕',
    'text.updateSubtitle': '更新字幕',
    'text.exportSrt': '导出SRT文件',
    'text.importingFile': '正在导入文件...',
    'text.importingRefVideo': '正在导入参考视频，请稍候...',
    'text.processingProgress': '处理进度',
    'text.realTimeSync': '实时同步已启用',
    'text.subtitleContentPlaceholder': '当前字幕内容将显示在这里...',
    'text.moveOffsetPlaceholder': '500',
    'text.speakerFromPlaceholder': 'speaker01',
    'text.speakerToPlaceholder': 'Mike',
    // AudioPanel
    'audio.localAudio': '本地音频',
    'audio.driveAudio': 'Drive音频',
    'audio.localJson': '本地JSON',
    'audio.driveJson': 'Drive JSON',
    'audio.importJson': '导入JSON',
    'audio.splitConfig': '切分配置',
    'audio.importing': '导入中...',
    'audio.importingAudio': '正在导入音频文件，请稍候...',
    // VideoPanel
    'video.localVideo': '本地视频',
    'video.driveVideo': 'Drive视频',
    'video.importing': '正在导入',
    'video.progress': '进度',
    'record.currentPosition': '当前播放头位置',
    'record.audioTextPlaceholder': '音频文本内容',
    'record.speedPlaceholder': '如 1.0',
    'record.styleGuidePromptPlaceholder': 'Style guide prompt',
    'record.memoPlaceholder': '输入备注信息',
    'record.noAudioSelected': '未选择音频',
    'record.cacheStatus': '缓存状态',
    'record.currentProject': '当前项目',
    'record.folders': '个文件夹',
    'record.notCached': '未缓存',
    'record.selectedItemPath': 'Selected item path',
    'record.projects': ' 个项目',
    'record.noReplaceAudio': '未选择替换音频',
    'record.refreshing': '刷新中...',
    'record.refresh': 'Refresh',
    'record.noRecordingData': '没有录音数据',
    'record.recordSavedInfer': '录音已保存，可直接点击 Infer 发送',
    'record.recordSaved': '录音已保存: {fileName}',
    'record.uploadedToTrack': '录音已上传并添加到轨道',
    'record.uploadFailed': '上传失败: {error}',
    'record.sessionSavedWithProject': '会话ID已保存，当前项目：{projectName}',
    'record.sessionSavedSelectFolder': '会话ID已保存，请选择项目文件夹',
    'record.needGetRef': '请先点击「Get Ref」获取参考音频，或选中带有 refName 的音频片段',
    'record.needRecordFirst': '请先录制音频',
    'record.needSessionLogin': '请先输入 Session ID 并点击 Login',
    'record.needSelectProjectFolder': '请先在 header 中选择项目文件夹',
    'record.inferComplete': '推理完成',
    'record.inferAudioAdded': '推理音频 {fileName} 已添加到轨道{driveSuffix}',
    'record.inferFailed': '推理失败: {error}',
    'record.cloneComplete': 'Clone 完成',
    'record.cloneAudioAdded': 'Clone 音频 {fileName} 已添加到轨道',
    'record.cloneFailed': 'Clone 失败: {error}',
    'record.noValidAudioData': '所选项没有有效的音频数据',
    'record.audioDataFetched': '音频数据获取成功',
    'record.fetchAudioDataFailed': '获取音频数据失败: {error}',
    'record.noAudioName': '未找到音频名称',
    'record.needFullProjectPath': '请先在 header 中选择完整的项目文件夹路径',
    'record.searchingAudioFiles': '正在搜索音频文件...',
    'record.noMatchingAudio': '未找到匹配的音频文件: {audioName}',
    'record.foundMatchingAudio': '找到 {count} 个匹配的音频文件',
    'record.refreshFailed': '刷新失败: {error}',
    'record.checkingCandidate': '正在检查候选音频...',
    'record.noCandidateAutoRefresh': '未检测到候选音频，正在尝试自动刷新...',
    'record.autoRefreshSucceeded': '自动刷新成功，已加载候选音频',
    'record.noCandidateImportJson': '无候选音频数据，请先导入 JSON 文件',
    'record.noCandidateOtherTracks': '当前轨道无候选音频，但另有 {count} 条轨道存在候选。请选择其他轨道或重新导入数据。',
    'record.networkRetry': '网络请求失败，正在重试 ({current}/{max})...',
    'record.candidateLoadedPlaying': '已加载并播放候选音频 ({current}/{total})',
    'record.candidateLoadStopped': '连续失败 {max} 次，已停止。请检查网络或稍后重试',
    'record.candidateLoadTryNext': '加载失败，正在尝试下一个候选...',
    'record.noAudioDataAvailable': '没有可用的音频数据',
    'record.needSelectTarget': '请先选择目标位置',
    'record.invalidTargetPosition': '目标位置无效',
    'record.audioReplaced': '音频已替换',
    'record.audioInserted': '音频已插入',
    'record.replaceAudioFailed': '替换音频失败',
    'record.textRetrieved': '文本内容获取成功',
    'record.getTextFailed': '获取音频文本失败: {error}',
    'record.needSelectTimelineItem': '请先选择时间轴上的音频或文本片段',
    'record.selectedItemInvalid': '所选项无效',
    'record.refNameNotFound': '未找到参考文件名',
    'record.refInfoFetched': '已获取参考文件名与 speaker',
    'record.fetchRefInfoFailed': '获取参考信息失败',
    'record.needTtsText': '请输入要合成的文本',
    'record.needValidSpeed': '请设置有效的播放速度',
    'record.ttsAudioAdded': 'TTS 音频 {fileName} 已添加到轨道{driveSuffix}',
    'record.ttsFailed': 'TTS 处理失败: {error}',
    'record.audioSelectedAtPosition': '已选择音频到位置 {index}',
    'record.refAudioUpdated': '位置 {index} 的参考音频名称已更新',
    'record.driveIdSuffix': ' (DriveID: {id})',
    'record.noDriveIdSuffix': ' (未获取到 DriveID)',
    // Menu items
    'menu.dashboard': '看板',
    'menu.text': '文字',
    'menu.voice': '语音',
    'menu.video': '视频',
    'menu.audio': '音频',
    'menu.record': '声音', // Changed from 录音 to 声音
    'menu.import': '导入', // New
    'menu.cache': '缓存',
    'dashboard.title': '看板',
    'dashboard.subtitle': 'Serverless GPU 与存储迁移状态（布局占位）',
    'dashboard.refresh': '刷新',
    'dashboard.refreshing': '刷新中…',
    'dashboard.lastChecked': '上次查看',
    'dashboard.neverChecked': '尚未刷新',
    'dashboard.refreshPlaceholder': '状态接口未接入，仅更新本地时间戳',
    'dashboard.actionPending': '接口占位：后续将调用 Runpod / R2 健康检查',
    'dashboard.phase.A': '阶段 A — Serverless 通路',
    'dashboard.phase.B': '阶段 B — R2 已联通',
    'dashboard.phase.C': '阶段 C — 移植 workflow',
    'dashboard.card.runpod': 'Runpod 端点',
    'dashboard.card.r2': 'Cloudflare R2',
    'dashboard.card.colab': 'Colab Session',
    'dashboard.card.project': '当前项目',
    'dashboard.cardHint.runpod': 'Queue worker · 实时状态待 API',
    'dashboard.cardHint.r2': '对象存储 · 实时状态待 API',
    'dashboard.cardHint.colab': 'Sound 面板 Session ID（现有链路）',
    'dashboard.cardHint.project': 'Header 所选盘符与项目文件夹',
    'dashboard.status.pendingApi': '待接入 API',
    'dashboard.section.serverless': 'Serverless GPU',
    'dashboard.section.storage': '存储与项目',
    'dashboard.section.roadmap': '迁移路线图',
    'dashboard.field.endpoint': '端点',
    'dashboard.field.endpointId': 'Endpoint ID',
    'dashboard.field.queueType': '类型',
    'dashboard.field.workers': 'Workers',
    'dashboard.field.r2Bucket': 'R2 Bucket',
    'dashboard.field.driveProject': 'Drive 项目',
    'dashboard.field.pipeline': 'Pipeline',
    'dashboard.field.colabSession': 'Session ID',
    'dashboard.workersPlaceholder': 'Min 0 / Max 1（配置占位）',
    'dashboard.action.ping': 'Ping Hello Worker',
    'dashboard.action.r2Smoke': 'R2 Smoke Test',
    'dashboard.projectUnset': '未选择项目文件夹',
    'dashboard.projectUntitled': '未命名项目',
    'dashboard.pipelineUnset': '未选择一级 Pipeline',
    'dashboard.sessionUnset': '未登录 Session',
    'dashboard.roadmapHint': '与 T4edit-serverlessGPU 迁移文档一致，勾选为已验证里程碑。',
    'dashboard.pillar.storage': '存储：Drive → R2',
    'dashboard.pillar.media': '媒体：HLS / DASH',
    'dashboard.pillar.compute': '算力：Colab → Serverless',
    'dashboard.roadmap.b1': 'R2 bucket 与凭证',
    'dashboard.roadmap.b2': 'Runpod 环境变量',
    'dashboard.roadmap.b3': 'Worker R2 smoke',
    'dashboard.roadmap.frontendR2': '前端物料改读 R2/CDN',
    'dashboard.roadmap.hls': '长媒体切片流水线',
    'dashboard.roadmap.player': 'hls.js / dash.js 播放器',
    'dashboard.roadmap.a': 'Hello Queue 通路',
    'dashboard.roadmap.b': 'Runpod ↔ R2',
    'dashboard.roadmap.c': 'Dub_Engines 步骤移植',
    'voice.title': 'Voice',
    'voice.refresh': '刷新',
    'voice.refreshing': '刷新中...',
    'voice.prev': '上一个音频',
    'voice.next': '下一个音频',
    'voice.play': '播放',
    'voice.roll': '切换',
    'voice.licence': 'Licence:',
    'voice.download': '下载',
    'voice.upload': '上传',
    'voice.index': '索引',
    'voice.emptySpeakers': '当前项目没有说话人。请先在文字面板加载项目，或在本面板 Auto Compose。',
    'voice.noProjectFolder': '请先选择共享盘和二级项目文件夹。',
    'voice.noVoicesFolder': '未找到 voices 文件夹（应与 audios、records、infers、object 同级）。',
    'voice.noRef': '没有匹配的参考音频',
    'voice.playFailed': '播放参考音频失败',
    'voice.downloadFailed': '下载参考音频失败',
    'voice.uploadFailed': '上传参考音频失败',
    'voice.uploadOk': '已上传 {name}',
    'voice.refreshFailed': '刷新 voices 失败',
    'voice.loaded': '说话人 {speakers} 个，voices 音频 {files} 个',
    'voice.rollPlaceholder': '切换下一候选（占位）',
    'voice.bookVoices': 'Book_Voices',
    'voice.voicesBooked': 'Voices_Booked',
    'voice.exportLicences': 'Export_Licences',
    'voice.exportNeedBook': '请先 Book_Voices 后再导出证书',
    'voice.exportLicenceEmpty': '没有可写入证书的角色',
    'voice.exportLicenceOk': '证书已导出',
    // CachePanel
    'cache.title': '缓存监视器',
    'cache.refresh': '刷新',
    'cache.refreshing': '刷新中...',
    'cache.clearMemory': '清内存',
    'cache.clearCache': '清缓存',
    'cache.blobUrlCache': 'BlobUrl缓存',
    'cache.itemsUnit': '项',
    'cache.waveformCache': '波形缓存',
    'cache.waveformsUnit': '个波形',
    'cache.filterAll': '所有类型',
    'cache.filterAudio': '音频缓存',
    'cache.filterVideo': '视频缓存',
    'cache.filterMedia': '媒体文件',
    'cache.sortById': '按ID排序',
    'cache.sortBySize': '按大小排序',
    'cache.sortByTimestamp': '按时间排序',
    'cache.sortByAccessTime': '按访问时间排序',
    'cache.searchPlaceholder': '搜索缓存键...',
    'cache.noData': '暂无缓存数据',
    'cache.deleteItem': '删除缓存项',
    'cache.cacheKey': '缓存键',
    'cache.refCount': '引用计数',
    'cache.lastAccess': '最后访问',
    'cache.timeRange': '时间范围',
    'cache.duplicateDetected': '检测到重复缓存!',
    'cache.duplicateCount': '相同缓存键出现 {count} 次',
    'cache.typeAudio': '音频',
    'cache.typeVideo': '视频',
    'cache.typeMedia': '媒体',
    'cache.typeOther': '其他',
    'cache.memoryClearSuccess': '内存清理完成',
    'cache.memoryClearFailed': '清理内存失败',
    'cache.cacheClearSuccess': '缓存清理完成',
    'cache.cacheClearFailed': '清理缓存失败',
    // Attribute Container
    'attr.clickToEdit': '点击轨道进行编辑',
    // Track List
    'track.dragToStart': '将素材拖拽到这里，开始编辑你的大作吧~',
    'track.split': '切分',
    'track.castShadow': 'Cast Shadow',
    'track.test': '测试',
    'track.waveform': '波形',
    'track.save': '保存',
    'track.undo': '撤回',
    'track.redo': '重做',
    'track.recover': '恢复',
    // TextPanel status messages
    'text.autoSaving': '自动保存中...',
    'text.autoSaved': '已自动保存',
    'text.unsavedChanges': '未保存的更改',
    'text.processing': '处理中...',
    'text.moveForward': '整体前拉←',
    'text.moveBackward': '整体后移→',
    // OAuth Callback
    'oauth.completingLogin': '正在完成登录',
    'oauth.pleaseWait': '请稍候...',
    // Common Messages
    'message.pleaseSelect': '请先选择',
    'message.pleaseSelectAudio': '请先选择音频',
    'message.pleaseSelectAudioItem': '请先选择音频项',
    'message.pleaseSelectSubtitle': '请先选择一个字幕',
    'message.pleaseSelectSubtitleTrack': '请选择一个字幕轨道',
    'message.pleaseSelectTarget': '请先选择目标位置',
    'message.pleaseSelectRefAudio': '请先选择参考音频',
    'message.pleaseSelectAudioFormat': '请选择音频格式文件',
    'message.pleaseSelectVideoFormat': '请选择视频格式文件',
    'message.pleaseSelectSrtFormat': '请选择SRT格式的字幕文件',
    'message.pleaseEnter': '请输入',
    'message.pleaseEnterText': '请输入要替换的文本',
    'message.pleaseEnterReplaceText': '请输入替换后的文本',
    'message.pleaseEnterValidTime': '请输入有效的移动时间（大于0）',
    'message.pleaseEnterSessionId': '请输入有效的会话ID',
    'message.pleaseEnterContent': '请输入内容',
    'message.pleaseEnterNumber': '请输入数字',
    'message.notFound': '未找到',
    'message.notFoundMatch': '未找到匹配的文本进行替换',
    'message.notFoundAnyFolder': '未找到任何备选文件夹',
    'message.notFoundTextContent': '未找到对应的文本内容',
    'message.operationSuccess': '操作成功',
    'message.operationFailed': '操作失败',
    'message.importSuccess': '导入成功',
    'message.importFailed': '导入失败',
    'message.exportSuccess': '导出成功',
    'message.exportFailed': '导出失败',
    'message.processingFailed': '处理失败',
    'message.canceled': '已取消',
    'message.canceledExport': '已取消导出，并定位到第一个未标识的字幕。',
    'message.canceledSelectFile': '已取消选择视频文件',
    'message.checkUnlabeledSubtitle': '检查到未被标识说话人的字幕，是否继续导出？',
    'message.projectExportSuccess': '工程文件导出成功',
    'message.subtitleImportSuccess': 'Subtitle import successful,共导入',
    'message.subtitleExportSuccess': 'SRT文件导出成功',
    'message.videoImportSuccess': '视频导入成功',
    'message.videoImportFailed': '视频导入失败',
    'message.driveVideoImportFailed': 'Drive视频导入失败',
    'message.driveRefVideoImportFailed': 'Drive参考视频导入失败',
    'message.driveFileImportFailed': 'Drive文件导入失败',
    'message.ttsProcessingFailed': 'TTS处理失败',
    'message.audioProcessingFailed': '音频片段处理失败',
    'message.opfsOperationFailed': 'OPFS操作失败',
    'message.unknownError': '未知错误',
    // Audio Attributes
    'attr.properties': '属性',
    'attr.basic': '基础',
    'attr.name': '名称',
    'attr.speaker': '说话人',
    'attr.refName': '参考原始音频名',
    'attr.driveId': 'Drive ID',
    'attr.candidateDriveIds': '候选 Drive IDs',
    'attr.orgText': '原始文本',
    'attr.text': '处理文本',
    'attr.note': '备注',
    'attr.isReference': '参考',
    'attr.silent': '静音',
    'attr.locked': '锁定',
    'attr.update': '更新',
    'attr.cancel': '取消',
    'attr.metadataUpdated': '已更新到当前项 metadata',
    // TextPanel extra
    'text.saveTo': '保存到: ',
    'text.pleaseImportJsonFirst': '请先从 Drive 导入 JSON 文件',
    'text.recorded': '已记录: '
  },
  en: {
    'lang.zh': '中文',
    'lang.en': 'EN',
    'header.saveProject': 'Save',
    'header.submitProject': 'Submit',
    'header.loadProject': 'Load',
    'header.logout': 'Logout',
    'header.selectSharedDrive': 'Select shared drive',
    'header.selectFirstLevelFolder': 'Select pipeline folder',
    'header.selectSecondLevelFolder': 'Select project folder',
    'header.autoCompose': 'auto-compose',
    'header.autoComposeImporting': 'Importing...',
    'header.noAutoSaveData': 'No auto-save data to recover',
    'header.restoreAutoSaveTitle': 'Restore auto-save',
    'header.restoreAutoSaveMessage': 'Restore auto-save from {time}? Unsaved changes will be lost.',
    'header.confirmRestore': 'Restore',
    'header.cancel': 'Cancel',
    'header.projectEmptyNoSave': 'Project is empty, nothing to save.',
    'header.projectSavedLocal': 'Project saved locally',
    'header.saveProjectFailed': 'Failed to save project',
    'header.projectEmptyNoSubmit': 'Project is empty, nothing to submit.',
    'header.selectDriveBeforeSubmit': 'Select shared drive / pipeline / project folder before submitting.',
    'header.noValidDriveTarget': 'No valid Drive target. Please reselect cloud location.',
    'header.projectSubmittedDrive': 'Project submitted to Drive',
    'header.submitProjectFailed': 'Failed to submit project to Drive',
    'header.loggedOut': 'Logged out',
    'player.reloggingIn': 'Re-logging in...',
    'header.selectDriveFirst': 'Please select Drive / Pipeline / Project first.',
    'header.noJsonInFolder': 'No JSON project files found in the selected folder.',
    'header.readingLatestFile': 'Reading latest file: {name}',
    'header.findingLatestProject': 'Finding latest project in Drive...',
    'header.downloadingFile': 'Downloading {name}...',
    'header.driveLoadFailed': 'Drive load failed',
    'header.unsupportedMp4InProject': 'Project contains unsupported MP4 video. Ensure all videos are WebM format.',
    'header.projectLoadSuccessDetail': 'Loaded {name}<br>Last edited by {user} at {time}',
    'header.projectLoadSuccess': 'Project loaded successfully',
    'header.projectLoadFailed': 'Failed to load project',
    'header.panelNoAutoCompose': 'Auto Compose is not supported on this panel',
    'header.loadingMediaFromCloud': 'Loading media from cloud, please wait...',
    'header.secondLevelFolderEmpty': 'Project folder is empty: {name}',
    'header.fileNotFound': 'File not found: {path}',
    'header.jsonFormatError': 'Invalid JSON format or empty data',
    'header.autoComposePartialFail': 'auto-compose finished with import errors:\n{errors}',
    'header.autoComposeComplete': 'auto-compose import complete',
    'header.autoComposeFailed': 'auto-compose import failed: {error}',
    'header.audioRestoredFromCache': 'Audio restored from cache: {name}',
    'header.audioStreamLoaded': 'Audio streamed from Drive: {name}',
    'header.audioStreamLoadedR2': 'Audio streamed from R2: {name}',
    'header.audioStreamLoadedDrive': 'Not on R2; audio streamed from Drive: {name}',
    'header.audioDownloaded': 'Audio downloaded from Drive: {name}',
    'header.audioDecodeFailed': 'Audio decode failed: {name}',
    'header.videoRestoredFromCache': 'Video restored from cache: {name}',
    'header.videoDownloaded': 'Video downloaded from Drive: {name}',
    'header.videoStreamLoadedR2': 'Video streamed from R2: {name}',
    'header.videoStreamLoadedDrive': 'Not on R2; video streamed from Drive: {name}',
    'header.largeMediaSourceSummary': 'Large media source',
    // RecordPanel -> Sound
    'record.login': 'Login',
    'record.record': 'Record',
    'record.stop': 'Stop',
    'record.play': 'Play',
    'record.fetch': 'Fetch',
    'record.fetching': 'Fetching...',
    'record.check': 'Check (Q)',
    'record.insert': 'Insert (W)',
    'record.rescan': 'Rescan',
    'record.clearCache': 'Clear',
    'record.exportInfo': 'Export',
    'record.getRef': 'Get Ref',
    'record.infer': 'Infer',
    'record.mimic': 'Mimic',
    'record.getText': 'Get Text',
    'record.speed': 'Speed',
    'record.tts': 'TTS',
    'record.speaker': 'Speaker',
    'record.currentSpeaker': 'current speaker',
    'record.pitch': 'Pitch',
    'record.autopitch': 'Auto',
    'record.clone': 'Clone',
    'record.get': 'Get',
    'record.send': 'Send',
    'record.scanning': 'Scanning...',
    'record.importing': 'Importing...',
    'record.processing': 'Processing...',
    // TextPanel
    'text.localSrt': 'Local SRT',
    'text.localImport': 'Local Import',
    'text.uploadSrt': 'Upload SRT',
    'text.importFromDrive': 'Drive Import',
    'text.importRefVideo': 'Import Ref',
    'text.driveRefVideo': 'Drive Ref',
    'text.addSubtitle': 'Add Sub',
    'text.splitSubtitle': 'Split Sub',
    'text.updateSubtitle': 'Update',
    'text.exportSrt': 'Export SRT',
    'text.importingFile': 'Importing...',
    'text.importingRefVideo': 'Importing Ref...',
    'text.processingProgress': 'Progress',
    'text.realTimeSync': 'Real-time Sync',
    'text.subtitleContentPlaceholder': 'Current subtitle content will be displayed here...',
    'text.moveOffsetPlaceholder': '500',
    'text.speakerFromPlaceholder': 'speaker01',
    'text.speakerToPlaceholder': 'Mike',
    // AudioPanel
    'audio.localAudio': 'Local Audio',
    'audio.driveAudio': 'Drive Audio',
    'audio.localJson': 'Local JSON',
    'audio.driveJson': 'Drive JSON',
    'audio.importJson': 'Import JSON',
    'audio.splitConfig': 'Split Config',
    'audio.importing': 'Importing...',
    'audio.importingAudio': 'Importing Audio...',
    // VideoPanel
    'video.localVideo': 'Local Video',
    'video.driveVideo': 'Drive Video',
    'video.importing': 'Importing',
    'video.progress': 'Progress',
    'record.currentPosition': 'Current Position',
    'record.audioTextPlaceholder': 'Audio Text Content',
    'record.speedPlaceholder': 'e.g. 1.0',
    'record.styleGuidePromptPlaceholder': 'Style guide prompt',
    'record.memoPlaceholder': 'Enter Memo Info',
    'record.noAudioSelected': 'No Audio Selected',
    'record.cacheStatus': 'Cache Status',
    'record.currentProject': 'Current Project',
    'record.folders': ' folders',
    'record.notCached': 'Not Cached',
    'record.selectedItemPath': 'Selected item path',
    'record.projects': ' projects',
    'record.noReplaceAudio': 'No Replace Audio',
    'record.refreshing': 'Refreshing...',
    'record.refresh': 'Refresh',
    'record.noRecordingData': 'No recording data',
    'record.recordSavedInfer': 'Recording saved, click Infer to send',
    'record.recordSaved': 'Recording saved: {fileName}',
    'record.uploadedToTrack': 'Recording uploaded and added to track',
    'record.uploadFailed': 'Upload failed: {error}',
    'record.sessionSavedWithProject': 'Session ID saved, current project: {projectName}',
    'record.sessionSavedSelectFolder': 'Session ID saved, please select project folder',
    'record.needGetRef': 'Click Get Ref first, or select an audio clip with refName',
    'record.needRecordFirst': 'Please record audio first',
    'record.needSessionLogin': 'Enter Session ID and click Login first',
    'record.needSelectProjectFolder': 'Select project folder in header first',
    'record.inferComplete': 'Inference complete',
    'record.inferAudioAdded': 'Inference audio {fileName} added to track{driveSuffix}',
    'record.inferFailed': 'Inference failed: {error}',
    'record.cloneComplete': 'Clone complete',
    'record.cloneAudioAdded': 'Clone audio {fileName} added to track',
    'record.cloneFailed': 'Clone failed: {error}',
    'record.noValidAudioData': 'Selected item has no valid audio data',
    'record.audioDataFetched': 'Audio data fetched successfully',
    'record.fetchAudioDataFailed': 'Failed to fetch audio data: {error}',
    'record.noAudioName': 'Audio name not found',
    'record.needFullProjectPath': 'Select full project folder path in header first',
    'record.searchingAudioFiles': 'Searching audio files...',
    'record.noMatchingAudio': 'No matching audio files: {audioName}',
    'record.foundMatchingAudio': 'Found {count} matching audio file(s)',
    'record.refreshFailed': 'Refresh failed: {error}',
    'record.checkingCandidate': 'Checking candidate audio...',
    'record.noCandidateAutoRefresh': 'No candidate audio detected, attempting auto refresh...',
    'record.autoRefreshSucceeded': 'Auto refresh succeeded, candidate audio loaded',
    'record.noCandidateImportJson': 'No candidate audio data. Please import JSON file first',
    'record.noCandidateOtherTracks': 'No candidate on current track, but {count} track(s) have candidates. Select another track or re-import.',
    'record.networkRetry': 'Network request failed, retrying ({current}/{max})...',
    'record.candidateLoadedPlaying': 'Loaded and playing candidate audio ({current}/{total})',
    'record.candidateLoadStopped': 'Failed {max} times in a row, stopping. Check network or try again later',
    'record.candidateLoadTryNext': 'Load failed, trying next candidate...',
    'record.noAudioDataAvailable': 'No audio data available',
    'record.needSelectTarget': 'Please select target position first',
    'record.invalidTargetPosition': 'Invalid target position',
    'record.audioReplaced': 'Audio replaced',
    'record.audioInserted': 'Audio inserted',
    'record.replaceAudioFailed': 'Failed to replace audio',
    'record.textRetrieved': 'Text content retrieved successfully',
    'record.getTextFailed': 'Failed to get audio text: {error}',
    'record.needSelectTimelineItem': 'Select an audio or text clip on the timeline first',
    'record.selectedItemInvalid': 'Selected item is invalid',
    'record.refNameNotFound': 'Reference file name not found',
    'record.refInfoFetched': 'Reference file name and speaker retrieved',
    'record.fetchRefInfoFailed': 'Failed to get reference info',
    'record.needTtsText': 'Enter text to synthesize',
    'record.needValidSpeed': 'Set a valid playback speed',
    'record.ttsAudioAdded': 'TTS audio {fileName} added to track{driveSuffix}',
    'record.ttsFailed': 'TTS processing failed: {error}',
    'record.audioSelectedAtPosition': 'Audio selected at position {index}',
    'record.refAudioUpdated': 'Reference audio name updated at position {index}',
    'record.driveIdSuffix': ' (DriveID: {id})',
    'record.noDriveIdSuffix': ' (DriveID not obtained)',
    // Menu items
    'menu.dashboard': 'Dashboard',
    'menu.text': 'Text',
    'menu.voice': 'Voice',
    'menu.video': 'Video',
    'menu.audio': 'Audio',
    'menu.record': 'Sound', // Changed from Record to Sound
    'menu.import': 'Import', // New
    'menu.cache': 'Cache',
    'dashboard.title': 'Dashboard',
    'dashboard.subtitle': 'Serverless GPU & storage migration (layout placeholder)',
    'dashboard.refresh': 'Refresh',
    'dashboard.refreshing': 'Refreshing…',
    'dashboard.lastChecked': 'Last viewed',
    'dashboard.neverChecked': 'Not refreshed yet',
    'dashboard.refreshPlaceholder': 'Status APIs not wired; timestamp updated locally only',
    'dashboard.actionPending': 'Placeholder: Runpod / R2 health checks coming later',
    'dashboard.phase.A': 'Phase A — Serverless path',
    'dashboard.phase.B': 'Phase B — R2 linked',
    'dashboard.phase.C': 'Phase C — Workflow port',
    'dashboard.card.runpod': 'Runpod endpoint',
    'dashboard.card.r2': 'Cloudflare R2',
    'dashboard.card.colab': 'Colab session',
    'dashboard.card.project': 'Current project',
    'dashboard.cardHint.runpod': 'Queue worker · live status pending API',
    'dashboard.cardHint.r2': 'Object storage · live status pending API',
    'dashboard.cardHint.colab': 'Session ID from Sound panel (legacy path)',
    'dashboard.cardHint.project': 'Drive + project folder from Header',
    'dashboard.status.pendingApi': 'API pending',
    'dashboard.section.serverless': 'Serverless GPU',
    'dashboard.section.storage': 'Storage & project',
    'dashboard.section.roadmap': 'Migration roadmap',
    'dashboard.field.endpoint': 'Endpoint',
    'dashboard.field.endpointId': 'Endpoint ID',
    'dashboard.field.queueType': 'Type',
    'dashboard.field.workers': 'Workers',
    'dashboard.field.r2Bucket': 'R2 bucket',
    'dashboard.field.driveProject': 'Drive project',
    'dashboard.field.pipeline': 'Pipeline',
    'dashboard.field.colabSession': 'Session ID',
    'dashboard.workersPlaceholder': 'Min 0 / Max 1 (config placeholder)',
    'dashboard.action.ping': 'Ping Hello Worker',
    'dashboard.action.r2Smoke': 'R2 Smoke Test',
    'dashboard.projectUnset': 'No project folder selected',
    'dashboard.projectUntitled': 'Untitled project',
    'dashboard.pipelineUnset': 'No pipeline folder selected',
    'dashboard.sessionUnset': 'No session logged in',
    'dashboard.roadmapHint': 'Aligned with T4edit-serverlessGPU migration docs; checks are verified milestones.',
    'dashboard.pillar.storage': 'Storage: Drive → R2',
    'dashboard.pillar.media': 'Media: HLS / DASH',
    'dashboard.pillar.compute': 'Compute: Colab → Serverless',
    'dashboard.roadmap.b1': 'R2 bucket & credentials',
    'dashboard.roadmap.b2': 'Runpod env vars',
    'dashboard.roadmap.b3': 'Worker R2 smoke',
    'dashboard.roadmap.frontendR2': 'Frontend reads R2/CDN',
    'dashboard.roadmap.hls': 'Long-media chunk pipeline',
    'dashboard.roadmap.player': 'hls.js / dash.js player',
    'dashboard.roadmap.a': 'Hello queue path',
    'dashboard.roadmap.b': 'Runpod ↔ R2',
    'dashboard.roadmap.c': 'Port Dub_Engines steps',
    'voice.title': 'Voice',
    'voice.refresh': 'Refresh',
    'voice.refreshing': 'Refreshing...',
    'voice.prev': 'Previous audio',
    'voice.next': 'Next audio',
    'voice.play': 'Play',
    'voice.roll': 'Roll',
    'voice.licence': 'Licence:',
    'voice.download': 'Download',
    'voice.upload': 'Upload',
    'voice.index': 'Index',
    'voice.emptySpeakers': 'No speakers in the current project. Load it from Text, or Auto Compose on this panel.',
    'voice.noProjectFolder': 'Select a shared drive and the project folder first.',
    'voice.noVoicesFolder': 'voices folder not found (sibling of audios, records, infers, object).',
    'voice.noRef': 'No matching reference audio',
    'voice.playFailed': 'Failed to play the reference audio',
    'voice.downloadFailed': 'Failed to download the reference audio',
    'voice.uploadFailed': 'Failed to upload the reference audio',
    'voice.uploadOk': 'Uploaded {name}',
    'voice.refreshFailed': 'Failed to refresh voices',
    'voice.loaded': '{speakers} speakers, {files} voice files',
    'voice.rollPlaceholder': 'Switch to next candidate (placeholder)',
    'voice.bookVoices': 'Book_Voices',
    'voice.voicesBooked': 'Voices_Booked',
    'voice.exportLicences': 'Export_Licences',
    'voice.exportNeedBook': 'Book voices before exporting the licence',
    'voice.exportLicenceEmpty': 'No characters to include in the certificate',
    'voice.exportLicenceOk': 'Licence certificate exported',
    // CachePanel
    'cache.title': 'Cache Monitor',
    'cache.refresh': 'Refresh',
    'cache.refreshing': 'Refreshing...',
    'cache.clearMemory': 'Clear Memory',
    'cache.clearCache': 'Clear Cache',
    'cache.blobUrlCache': 'BlobUrl Cache',
    'cache.itemsUnit': 'items',
    'cache.waveformCache': 'Waveform Cache',
    'cache.waveformsUnit': 'waveforms',
    'cache.filterAll': 'All Types',
    'cache.filterAudio': 'Audio Cache',
    'cache.filterVideo': 'Video Cache',
    'cache.filterMedia': 'Media Files',
    'cache.sortById': 'Sort by ID',
    'cache.sortBySize': 'Sort by Size',
    'cache.sortByTimestamp': 'Sort by Timestamp',
    'cache.sortByAccessTime': 'Sort by Access Time',
    'cache.searchPlaceholder': 'Search cache keys...',
    'cache.noData': 'No cache data',
    'cache.deleteItem': 'Delete cache item',
    'cache.cacheKey': 'Cache Key',
    'cache.refCount': 'Ref Count',
    'cache.lastAccess': 'Last Access',
    'cache.timeRange': 'Time Range',
    'cache.duplicateDetected': 'Duplicate cache detected!',
    'cache.duplicateCount': 'Same cache key appears {count} times',
    'cache.typeAudio': 'Audio',
    'cache.typeVideo': 'Video',
    'cache.typeMedia': 'Media',
    'cache.typeOther': 'Other',
    'cache.memoryClearSuccess': 'Memory cleared',
    'cache.memoryClearFailed': 'Failed to clear memory',
    'cache.cacheClearSuccess': 'Cache cleared',
    'cache.cacheClearFailed': 'Failed to clear cache',
    // Attribute Container
    'attr.clickToEdit': 'Click track to edit',
    // Track List
    'track.dragToStart': 'Drag materials here to start editing your masterpiece~',
    'track.split': 'Split',
    'track.castShadow': 'Cast Shadow',
    'track.test': 'Test',
    'track.waveform': 'Wave',
    'track.save': 'Save',
    'track.undo': 'Undo',
    'track.redo': 'Redo',
    'track.recover': 'Recover',
    // TextPanel status messages
    'text.autoSaving': 'Auto Saving...',
    'text.autoSaved': 'Auto Saved',
    'text.unsavedChanges': 'Unsaved Changes',
    'text.processing': 'Processing...',
    'text.moveForward': 'Move Forward←',
    'text.moveBackward': 'Move Backward→',
    // OAuth Callback
    'oauth.completingLogin': 'Completing Login',
    'oauth.pleaseWait': 'Please wait...',
    // Common Messages
    'message.pleaseSelect': 'Please select',
    'message.pleaseSelectAudio': 'Please select audio',
    'message.pleaseSelectAudioItem': 'Please select audio item',
    'message.pleaseSelectSubtitle': 'Please select a subtitle',
    'message.pleaseSelectSubtitleTrack': 'Please select a subtitle track',
    'message.pleaseSelectTarget': 'Please select target position',
    'message.pleaseSelectRefAudio': 'Please select reference audio',
    'message.pleaseSelectAudioFormat': 'Please select audio format file',
    'message.pleaseSelectVideoFormat': 'Please select video format file',
    'message.pleaseSelectSrtFormat': 'Please select SRT format subtitle file',
    'message.pleaseEnter': 'Please enter',
    'message.pleaseEnterText': 'Please enter text to replace',
    'message.pleaseEnterReplaceText': 'Please enter replacement text',
    'message.pleaseEnterValidTime': 'Please enter valid move time (greater than 0)',
    'message.pleaseEnterSessionId': 'Please enter valid session ID',
    'message.pleaseEnterContent': 'Please enter content',
    'message.pleaseEnterNumber': 'Please enter number',
    'message.notFound': 'Not found',
    'message.notFoundMatch': 'No matching text found for replacement',
    'message.notFoundAnyFolder': 'No alternative folders found',
    'message.notFoundTextContent': 'No corresponding text content found',
    'message.operationSuccess': 'Operation successful',
    'message.operationFailed': 'Operation failed',
    'message.importSuccess': 'Import successful',
    'message.importFailed': 'Import failed',
    'message.exportSuccess': 'Export successful',
    'message.exportFailed': 'Export failed',
    'message.processingFailed': 'Processing failed',
    'message.canceled': 'Canceled',
    'message.canceledExport': 'Export canceled and positioned to first unlabeled subtitle.',
    'message.canceledSelectFile': 'Video file selection canceled',
    'message.checkUnlabeledSubtitle': 'Unlabeled speaker subtitles detected, continue export?',
    'message.projectExportSuccess': 'Project file exported successfully',
    'message.subtitleImportSuccess': 'Subtitle import successful, imported',
    'message.subtitleExportSuccess': 'SRT file exported successfully',
    'message.videoImportSuccess': 'Video import successful',
    'message.videoImportFailed': 'Video import failed',
    'message.driveVideoImportFailed': 'Drive video import failed',
    'message.driveRefVideoImportFailed': 'Drive reference video import failed',
    'message.driveFileImportFailed': 'Drive file import failed',
    'message.ttsProcessingFailed': 'TTS processing failed',
    'message.audioProcessingFailed': 'Audio clip processing failed',
    'message.opfsOperationFailed': 'OPFS operation failed',
    'message.unknownError': 'Unknown error',
    // Audio Attributes
    'attr.properties': 'Properties',
    'attr.basic': 'Basic',
    'attr.name': 'Name',
    'attr.speaker': 'Speaker',
    'attr.refName': 'Ref audio name',
    'attr.driveId': 'Drive ID',
    'attr.candidateDriveIds': 'Candidate Drive IDs',
    'attr.orgText': 'Original Text',
    'attr.text': 'Processed Text',
    'attr.note': 'Note',
    'attr.isReference': 'Reference',
    'attr.silent': 'Silent',
    'attr.locked': 'Locked',
    'attr.update': 'Update',
    'attr.cancel': 'Cancel',
    'attr.metadataUpdated': 'Updated to current item metadata',
    // TextPanel extra
    'text.saveTo': 'Save to: ',
    'text.pleaseImportJsonFirst': 'Please import JSON file from Drive first',
    'text.recorded': 'Recorded: '
  }
};

const systemDefault = navigator.language && navigator.language.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en';
const initialLocale = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || systemDefault;

const localeRef = ref<string>(initialLocale);

function setLocale(next: string) {
  localeRef.value = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // 忽略localStorage错误
  }
}

function translate(key: string): string {
  const dict = zhCn[localeRef.value] || {};
  return key in dict ? dict[key] : key;
}

function formatTranslate(key: string, params?: Record<string, string | number>): string {
  let text = translate(key);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return text;
}

export function useLocale(): { locale: Ref<string>; setLocale: (next: string) => void } {
  return { locale: localeRef, setLocale };
}

// 添加 useI18n 函数用于 Composition API
export function useI18n() {
  return {
    t: translate,
    tf: formatTranslate,
    locale: localeRef,
    setLocale
  };
}

/** 非组件模块（Drive Ref / Auto Compose runner）与 ElMessage 提示共用 */
export function tf(key: string, params?: Record<string, string | number>): string {
  return formatTranslate(key, params);
}

const I18nPlugin = {
  install(app: App) {
    // 全局 $t
    (app.config.globalProperties as any).$t = translate;
    // provide 当前语言与切换方法（可选注入使用）
    app.provide('app.locale', localeRef);
    app.provide('app.setLocale', setLocale);
    app.provide('i18n', { t: translate, tf: formatTranslate, locale: localeRef, setLocale });
  }
};

export default I18nPlugin;
